// Agregaciones de la sección Reportes: la foto completa del venue en un
// período — dinero, eventos, embudo comercial y web. Todo es un scan en
// memoria sobre colecciones que VenueProvider ya tiene suscritas: cero
// lecturas extra, cero colecciones nuevas.
//
// DOS NOCIONES DE DINERO QUE NO HAY QUE MEZCLAR:
//   - Utilidad de eventos: valor de los eventos del período MENOS sus costos
//     (vengan del mes que vengan — el costo pertenece al evento). Contesta
//     "¿fue negocio lo que hice este mes?".
//   - Neto de caja: pagos − reembolsos − costos de caja del período.
//     Contesta "¿cuánto dinero entró y salió este mes?".
// Un evento de diciembre cobrado en enero hace que las dos difieran, y eso
// es correcto: son preguntas distintas.

import { getValorEvento } from "./valorEvento";
import { asientosDeDeposito } from "./ledger";
import { isInRange, calendarDayAnchor, INSTANTE } from "./dates";
import { isContactFormLead } from "./leadHelpers";
import { llevaInventario, unidadDe } from "./catalogo";
import { resumenCliente, nombreCliente } from "./clientes";
import { costosPorEvento, fechaPagoDe, esComisionPlanner, devolucionDe } from "./costos";

const monto = (x) => Number(x?.monto) || 0;

// Variación relativa vs. el período anterior. null = no calculable (sin base
// de comparación); la UI lo pinta como "—" y no como 0%.
export const variacion = (actual, anterior) => {
  const prev = Number(anterior);
  if (!isFinite(prev) || prev === 0) return null;
  return (Number(actual) - prev) / Math.abs(prev);
};

// ── Dinero (caja) ──────────────────────────────────────────────────────
// Los costos cuentan cuando el dinero SALIÓ (FRA-342/346): solo pagados,
// anclados por su fecha de pago. Un costo por pagar no cuenta en ningún
// mes; uno causado en diciembre y pagado en enero cuenta en enero.
// `depositos` (opcional, las dos fuentes mezcladas) entra al flujo:
// recibirlos es entrada y devolverlos salida; aplicarlos no mueve caja pero
// SÍ es ingreso del venue (`depositosAplicados`): el dinero ya estaba en la
// cuenta como custodia y ese día pasa a ser propio. Va aparte de `cobrado`
// y de `neto` para no contaminar la utilidad de los eventos.
// `retiros` (colección propia, FRA-343) también salen de caja, pero NO son
// costo: bajan el flujo neto sin tocar `neto`, que sigue siendo el
// resultado operativo del período.
export function resumenDinero({ payments = [], refunds = [], costos = [], depositos = [], retiros = [], desde, hasta, timeZone }) {
  const enRango = (x) => isInRange(x?.fecha, desde, hasta, timeZone);
  const cobrado = payments.filter(enRango).reduce((s, p) => s + monto(p), 0);
  const reembolsos = refunds.filter(enRango).reduce((s, r) => s + monto(r), 0);
  // Lo por pagar todavía no sale: solo cuenta lo pagado, por su fecha de
  // pago — misma regla que el neto de Cuentas.
  const costosBrutos = costos
    .filter((c) => isInRange(fechaPagoDe(c), desde, hasta, timeZone))
    .reduce((s, c) => s + monto(c), 0);
  // Lo que un proveedor devolvió de un evento cancelado (saldo a favor)
  // revierte el gasto en el mes en que regresó: el gasto del período es neto.
  const devolucionesProveedor = costos.reduce((s, c) => {
    const d = devolucionDe(c);
    return d && isInRange(d.fecha, desde, hasta, timeZone) ? s + d.monto : s;
  }, 0);
  const costosCaja = costosBrutos - devolucionesProveedor;

  let depositosEntrada = 0;
  let depositosSalida = 0;
  let depositosAplicados = 0;
  (depositos || []).forEach((dep) => {
    asientosDeDeposito(dep).forEach((a) => {
      if (!isInRange(a.fecha, desde, hasta, timeZone)) return;
      if (a.tipo === "deposito_aplicado") depositosAplicados += a.monto;
      else if (a.caja > 0) depositosEntrada += a.caja;
      else if (a.caja < 0) depositosSalida += -a.caja;
    });
  });

  const retirosTotal = (retiros || []).filter(enRango).reduce((s, r) => s + monto(r), 0);

  const neto = cobrado - reembolsos - costosCaja;
  return {
    cobrado,
    reembolsos,
    costosCaja,
    costosBrutos,
    devolucionesProveedor,
    neto,
    depositosEntrada,
    depositosSalida,
    depositosAplicados,
    retiros: retirosTotal,
    // El flujo completo: también el dinero en custodia que entró y salió, y
    // lo que el dueño retiró.
    flujoNeto: neto + depositosEntrada - depositosSalida - retirosTotal,
    pagosCount: payments.filter(enRango).length,
  };
}

// ── Eventos ────────────────────────────────────────────────────────────
export function resumenEventos({ events = [], costos = [], refunds = [], desde, hasta, timeZone }) {
  const costosDeEventos = costosPorEvento(costos);
  const delPeriodo = events.filter(
    (e) =>
      e?.eventType === "event" && isInRange(e.startTime, desde, hasta, timeZone, INSTANTE)
  );
  const canceladosDelPeriodo = delPeriodo.filter((e) => e.status === "cancelado");
  const cancelados = canceladosDelPeriodo.length;
  const activos = delPeriodo.filter((e) => e.status !== "cancelado");

  // Un cancelado no es venta, pero el venue pudo quedarse con parte de lo
  // cobrado: lo retenido es lo pagado menos lo reembolsado. Los reembolsos
  // se leen de su colección y no del contador del evento, que puede estar
  // desfasado. Van aparte de `porEvento` para no tocar valor ni utilidad.
  const reembolsadoPorEvento = {};
  (refunds || []).forEach((r) => {
    if (r?.eventId) reembolsadoPorEvento[r.eventId] = (reembolsadoPorEvento[r.eventId] || 0) + monto(r);
  });
  const eventosCancelados = canceladosDelPeriodo.map((e) => {
    const pagado = Number(e.totalPagado || 0);
    const reembolsado = reembolsadoPorEvento[e.id] || 0;
    return {
      id: e.id,
      nombre: e.nombre || "Sin nombre",
      startTime: e.startTime,
      cancelado: true,
      pagado,
      reembolsado,
      retenido: Math.max(0, pagado - reembolsado),
      costos: costosDeEventos[e.id]?.total || 0,
    };
  });

  // Pasados vs. por realizar: un período que llega al futuro ("Este año",
  // "Este mes") incluye eventos ya vendidos que todavía no ocurren. Siguen
  // contando en su mes — es la vista por evento — pero se desglosan para no
  // presentarlos como realizados. El evento de HOY ya cuenta como realizado.
  const hoy = calendarDayAnchor(new Date(), timeZone, INSTANTE);
  let valor = 0;
  let costosTotal = 0;
  let realizados = 0;
  let valorPorRealizar = 0;
  let utilidadPorRealizar = 0;
  const porEvento = activos.map((e) => {
    const v = getValorEvento(e);
    const c = costosDeEventos[e.id]?.total || 0;
    const futuro = calendarDayAnchor(e.startTime, timeZone, INSTANTE) > hoy;
    valor += v;
    costosTotal += c;
    if (futuro) {
      valorPorRealizar += v;
      utilidadPorRealizar += v - c;
    } else {
      realizados += 1;
    }
    // Lo cobrado es el mismo `totalPagado` que leen Cuentas por cobrar y
    // Cartera: los pagos del evento, sin importar en qué mes llegaron.
    const pagado = Number(e.totalPagado || 0);
    return {
      id: e.id,
      nombre: e.nombre || "Sin nombre",
      startTime: e.startTime,
      valor: v,
      pagado,
      saldo: Math.max(0, v - pagado),
      costos: c,
      utilidad: v - c,
      futuro,
    };
  });

  const utilidad = valor - costosTotal;
  return {
    eventos: activos.length,
    realizados,
    porRealizar: activos.length - realizados,
    valorPorRealizar,
    utilidadPorRealizar,
    cancelados,
    eventosCancelados,
    valor,
    costos: costosTotal,
    utilidad,
    margen: valor > 0 ? utilidad / valor : null,
    valorPromedio: activos.length > 0 ? valor / activos.length : 0,
    // Para la tabla de mejores/peores: ordenados por utilidad, desc.
    porEvento: porEvento.sort((a, b) => b.utilidad - a.utilidad),
  };
}

// ── Cancelaciones ──────────────────────────────────────────────────────
// Lo que el venue se quedó de un evento cancelado: lo cobrado menos lo
// reembolsado, menos los egresos que ese evento sí generó. No es utilidad
// de ningún evento (no ocurrió) pero sí resultado del venue — mismo trato
// que un depósito aplicado: columna propia que el Resultado suma.
// Cuenta el mes en que se canceló (`cancelledAt`), que es cuando ese dinero
// deja de ser anticipo; sin esa fecha, el del evento. Los reembolsos se
// leen de su colección: el contador del evento puede estar desfasado.
export function cancelacionesRetenidas({ events = [], costos = [], refunds = [], desde, hasta, timeZone }) {
  const costosDeEventos = costosPorEvento(costos);
  const reembolsado = {};
  (refunds || []).forEach((r) => {
    if (r?.eventId) reembolsado[r.eventId] = (reembolsado[r.eventId] || 0) + monto(r);
  });
  const lista = (events || [])
    .filter((e) => e?.eventType === "event" && e.status === "cancelado")
    .map((e) => {
      const fecha = e.cancelledAt || e.startTime;
      const pagado = Number(e.totalPagado || 0);
      const reemb = reembolsado[e.id] || 0;
      const retenido = Math.max(0, pagado - reemb);
      const costosEv = costosDeEventos[e.id]?.total || 0;
      return {
        id: e.id,
        nombre: e.nombre || "Sin nombre",
        fecha,
        startTime: e.startTime,
        pagado,
        reembolsado: reemb,
        retenido,
        costos: costosEv,
        resultado: retenido - costosEv,
      };
    })
    .filter(
      (c) =>
        (c.retenido > 0.01 || c.costos > 0.01) &&
        isInRange(c.fecha, desde, hasta, timeZone, INSTANTE)
    );
  return { total: lista.reduce((s, c) => s + c.resultado, 0), lista };
}

// ── Embudo comercial (leads) ───────────────────────────────────────────
// Cohorte por fecha de llegada: "de los leads que llegaron en el período,
// cuántos van ganados". Es la lectura honesta para comparar períodos —
// mezclar leads viejos ganados hoy inflaría el mes actual.
export function resumenLeads({ contactForms = [], desde, hasta, timeZone }) {
  const delPeriodo = contactForms.filter((f) =>
    isInRange(f?.createdAt, desde, hasta, timeZone)
  );
  const ganados = delPeriodo.filter((f) => f.status === "ganado").length;
  const perdidos = delPeriodo.filter((f) => f.status === "perdido").length;

  const origenes = {};
  delPeriodo.forEach((f) => {
    const origen = isContactFormLead(f.origin) ? "Venueboard" : String(f.origin).trim();
    const acc = origenes[origen] || (origenes[origen] = { origen, count: 0 });
    acc.count += 1;
  });

  return {
    recibidos: delPeriodo.length,
    ganados,
    perdidos,
    tasaGanados: delPeriodo.length > 0 ? ganados / delPeriodo.length : null,
    porOrigen: Object.values(origenes).sort((a, b) => b.count - a.count),
  };
}

// ── Web ────────────────────────────────────────────────────────────────
// Misma fórmula que Analíticas web: contacto = formularios web + clics a
// redes (un clic a WhatsApp/IG es intención de contacto), sobre visitas.
export function resumenWeb({ loads = [], socialClicks = [], contactForms = [], desde, hasta, timeZone }) {
  const visitas = loads.filter((l) => isInRange(l?.timestamp, desde, hasta, timeZone)).length;
  const clics = socialClicks.filter((c) => isInRange(c?.timestamp, desde, hasta, timeZone)).length;
  const formularios = contactForms.filter(
    (f) => isContactFormLead(f?.origin) && isInRange(f?.createdAt, desde, hasta, timeZone)
  ).length;
  const contacto = formularios + clics;
  return {
    visitas,
    clics,
    formularios,
    contacto,
    pctContacto: visitas > 0 ? contacto / visitas : null,
  };
}

// ── Serie mensual (para las gráficas de 12 meses) ─────────────────────
// `meses` es una lista de "YYYY-MM" que la página arma (últimos 12); aquí
// solo se bucketiza para no duplicar la lógica de rangos.
export function serieMensual({ meses = [], events = [], payments = [], refunds = [], costos = [], depositos = [], retiros = [], contactForms = [], timeZone, getMonthRange }) {
  const costosDeEventos = costosPorEvento(costos);
  return meses.map((key) => {
    const { from, to } = getMonthRange(key);
    const dinero = resumenDinero({ payments, refunds, costos, depositos, retiros, desde: from, hasta: to, timeZone });
    const activos = events.filter(
      (e) =>
        e?.eventType === "event" &&
        e.status !== "cancelado" &&
        isInRange(e.startTime, from, to, timeZone, INSTANTE)
    );
    let valor = 0;
    let costosEventos = 0;
    activos.forEach((e) => {
      valor += getValorEvento(e);
      costosEventos += costosDeEventos[e.id]?.total || 0;
    });
    const leads = contactForms.filter((f) => isInRange(f?.createdAt, from, to, timeZone)).length;
    const cancelaciones = cancelacionesRetenidas({ events, costos, refunds, desde: from, hasta: to, timeZone }).total;
    return {
      key,
      cobrado: dinero.cobrado,
      reembolsos: dinero.reembolsos,
      costos: dinero.costosCaja,
      neto: dinero.neto,
      depositosEntrada: dinero.depositosEntrada,
      depositosSalida: dinero.depositosSalida,
      depositosAplicados: dinero.depositosAplicados,
      retiros: dinero.retiros,
      flujoNeto: dinero.flujoNeto,
      eventos: activos.length,
      valor,
      utilidad: valor - costosEventos,
      cancelaciones,
      leads,
    };
  });
}

// ── Estacionalidad ─────────────────────────────────────────────────────
// Qué meses del año dejan más, sumando TODOS los años: la utilidad se
// atribuye al mes en que OCURRE el evento (startTime), no al mes en que se
// cobró — las reservas de abril pueden empezar a pagar desde junio del año
// anterior, y eso es flujo de caja, no estacionalidad. Solo eventos ya
// ocurridos: un futuro a medio vender diría que "el mes que viene es malo".
export function estacionalidadMensual({ events = [], costos = [], timeZone, ahora = new Date() }) {
  const costosDeEventos = costosPorEvento(costos);
  const meses = Array.from({ length: 12 }, (_, i) => ({
    mes: i,
    eventos: 0,
    valor: 0,
    utilidad: 0,
    lista: [],
    anios: new Set(),
  }));
  (events || []).forEach((e) => {
    if (e?.eventType !== "event" || e.status === "cancelado") return;
    const anchor = calendarDayAnchor(e.startTime, timeZone, INSTANTE);
    if (!anchor) return;
    const inicio = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
    if (!(inicio <= ahora)) return;
    const valor = getValorEvento(e);
    const utilidad = valor - (costosDeEventos[e.id]?.total || 0);
    const acc = meses[anchor.getUTCMonth()];
    acc.eventos += 1;
    acc.valor += valor;
    acc.utilidad += utilidad;
    acc.anios.add(anchor.getUTCFullYear());
    acc.lista.push({
      id: e.id,
      nombre: e.nombre || "Sin nombre",
      startTime: e.startTime,
      valor,
      utilidad,
    });
  });
  const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
  return meses.map(({ anios, lista, ...m }) => ({
    ...m,
    anios: anios.size,
    promedio: m.eventos > 0 ? m.utilidad / m.eventos : 0,
    lista: lista.sort((a, b) => t(b.startTime) - t(a.startTime)),
  }));
}

// La misma lectura, pero sin juntar años: un bloque por año (descendente),
// cada uno con sus 12 meses. Para comparar agosto contra agosto sin que la
// suma esconda cuál año fue el bueno.
export function estacionalidadPorAnio({ events = [], costos = [], timeZone, ahora = new Date() }) {
  const costosDeEventos = costosPorEvento(costos);
  const porAnio = {};
  (events || []).forEach((e) => {
    if (e?.eventType !== "event" || e.status === "cancelado") return;
    const anchor = calendarDayAnchor(e.startTime, timeZone, INSTANTE);
    if (!anchor) return;
    const inicio = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
    if (!(inicio <= ahora)) return;
    const y = anchor.getUTCFullYear();
    const meses =
      porAnio[y] ||
      (porAnio[y] = Array.from({ length: 12 }, (_, i) => ({
        mes: i,
        eventos: 0,
        valor: 0,
        utilidad: 0,
        lista: [],
      })));
    const valor = getValorEvento(e);
    const utilidad = valor - (costosDeEventos[e.id]?.total || 0);
    const acc = meses[anchor.getUTCMonth()];
    acc.eventos += 1;
    acc.valor += valor;
    acc.utilidad += utilidad;
    acc.lista.push({
      id: e.id,
      nombre: e.nombre || "Sin nombre",
      startTime: e.startTime,
      valor,
      utilidad,
    });
  });
  const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
  return Object.keys(porAnio)
    .map(Number)
    .sort((a, b) => b - a)
    .map((anio) => ({
      anio,
      meses: porAnio[anio].map((m) => ({
        ...m,
        promedio: m.eventos > 0 ? m.utilidad / m.eventos : 0,
        lista: m.lista.sort((a, b) => t(b.startTime) - t(a.startTime)),
      })),
    }));
}

// Desglose de caja de un mes: los movimientos que componen las cifras de
// serieMensual — mismas reglas de rango y de "solo caja", para que las
// listas cuadren centavo a centavo con las columnas.
export function detalleMensual({ payments = [], refunds = [], costos = [], depositos = [], retiros = [], events = [], desde, hasta, timeZone }) {
  const enRango = (x) => isInRange(x?.fecha, desde, hasta, timeZone);
  const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
  const porFecha = (a, b) => t(a.fecha) - t(b.fecha);
  const nombreEvento = {};
  (events || []).forEach((e) => {
    if (e?.id) nombreEvento[e.id] = e.nombre || "Sin nombre";
  });

  const pagos = (payments || [])
    .filter(enRango)
    .map((p) => ({
      id: p.id,
      fecha: p.fecha,
      monto: monto(p),
      metodo: p.metodo || null,
      eventId: p.eventId || null,
      eventNombre: p.eventId ? nombreEvento[p.eventId] || null : null,
    }))
    .sort(porFecha);

  const reembolsos = (refunds || [])
    .filter(enRango)
    .map((r) => ({
      id: r.id,
      fecha: r.fecha,
      monto: monto(r),
      metodo: r.metodo || null,
      eventId: r.eventId || null,
      eventNombre: r.eventId ? nombreEvento[r.eventId] || null : null,
    }))
    .sort(porFecha);

  // Solo lo pagado, con la fecha en que salió el dinero (FRA-342): un costo
  // por pagar no es movimiento y no aparece en ningún mes.
  const costosCaja = (costos || [])
    .filter((c) => isInRange(fechaPagoDe(c), desde, hasta, timeZone))
    .map((c) => ({
      id: c.id,
      fecha: fechaPagoDe(c),
      fechaCausacion: c.fecha,
      monto: monto(c),
      concepto: c.concepto || "—",
      categoria: c.categoria || null,
      metodo: c.metodo || null,
      estado: c.estado || null,
      eventId: c.eventId || null,
      eventNombre: c.eventId ? nombreEvento[c.eventId] || null : null,
    }))
    // Devoluciones de proveedor (saldo a favor de un evento cancelado que
    // regresó): filas negativas en el mes en que entró el dinero.
    .concat(
      (costos || [])
        .map((c) => ({ c, d: devolucionDe(c) }))
        .filter(({ d }) => d && isInRange(d.fecha, desde, hasta, timeZone))
        .map(({ c, d }) => ({
          id: `devolucion-${c.id}`,
          tipo: "devolucion",
          fecha: d.fecha,
          fechaCausacion: c.fecha,
          monto: -d.monto,
          concepto: `Devolución de ${c.proveedorNombre || "proveedor"}`,
          categoria: c.categoria || null,
          metodo: c.saldoAFavor?.metodoDevolucion || null,
          estado: c.estado || null,
          eventId: c.eventId || null,
          eventNombre: c.eventId ? nombreEvento[c.eventId] || null : null,
        }))
    )
    .sort(porFecha);

  // Depósitos que movieron caja en el mes (recibidos y devueltos, de las
  // dos fuentes). La aplicación también se lista, con `aplicado: true` y
  // caja en cero: no mueve dinero, pero es el día en que el depósito dejó de
  // ser custodia y pasó a ser ingreso — sin ella el mes se lee incompleto.
  const depositosMes = [];
  (depositos || []).forEach((dep) => {
    asientosDeDeposito(dep).forEach((a) => {
      if (!isInRange(a.fecha, desde, hasta, timeZone)) return;
      depositosMes.push({
        id: a.id,
        fecha: a.fecha,
        monto: a.caja,
        aplicado: a.tipo === "deposito_aplicado",
        tipo: a.tipo,
        concepto: a.concepto,
        detalle: a.detalle,
        proveedorNombre: a.proveedorNombre || null,
        parcial: !!a.parcial,
        montoAplicado: a.montoAplicado || 0,
        eventId: a.eventId,
        eventNombre: a.eventId ? nombreEvento[a.eventId] || null : null,
        _orden: a.orden || 0,
      });
    });
  });
  // Mismo día: recibido antes que su desenlace (comparten createdAt).
  depositosMes.sort((a, b) => porFecha(a, b) || a._orden - b._orden);

  // Retiros del dueño: salen de caja sin ser gasto.
  const retirosMes = (retiros || [])
    .filter(enRango)
    .map((r) => ({
      id: r.id,
      fecha: r.fecha,
      monto: monto(r),
      concepto: r.concepto || "Retiro",
      metodo: r.metodo || null,
    }))
    .sort(porFecha);

  return { pagos, reembolsos, costosCaja, depositos: depositosMes, retiros: retirosMes };
}

// ── Caja por método de pago ────────────────────────────────────────────
// Los mismos movimientos de Flujo de caja (detalleMensual), agrupados por
// cómo se movió el dinero: contesta "¿cuánto efectivo debería tener?". El
// neto de todos los métodos cuadra con el flujo neto del período.
// El nombre se agrupa sin mayúsculas ni espacios ("Transferencia " y
// "transferencia" son uno) y se muestra como lo nombra el catálogo del
// venue cuando existe ahí. La aplicación de un depósito no mueve caja y no
// entra.
export const SIN_METODO = "__sin_metodo__";
const claveMetodo = (m) => String(m || "").trim().toLowerCase() || SIN_METODO;

export function cajaPorMetodo({ catalogo = [], ...movs }) {
  const d = detalleMensual(movs);
  const nombres = {};
  (catalogo || []).forEach((m) => {
    if (m?.nombre) nombres[claveMetodo(m.nombre)] = m.nombre.trim();
  });
  const filas = {};
  const fila = (metodo) => {
    const key = claveMetodo(metodo);
    if (!filas[key]) {
      filas[key] = {
        key,
        nombre: key === SIN_METODO ? "Sin método" : nombres[key] || String(metodo).trim(),
        ingresos: 0,
        pagosCount: 0,
        reembolsos: 0,
        egresos: 0,
        egresosCount: 0,
        depositos: 0,
        retiros: 0,
        movimientos: 0,
      };
    }
    filas[key].movimientos += 1;
    return filas[key];
  };
  d.pagos.forEach((p) => {
    const f = fila(p.metodo);
    f.ingresos += p.monto;
    f.pagosCount += 1;
  });
  d.reembolsos.forEach((r) => {
    fila(r.metodo).reembolsos += r.monto;
  });
  // Egresos netos de devoluciones de proveedor (filas negativas), igual que
  // el gasto de caja del período.
  d.costosCaja.forEach((c) => {
    const f = fila(c.metodo);
    f.egresos += c.monto;
    if (c.monto > 0) f.egresosCount += 1;
  });
  (movs.depositos || []).forEach((dep) => {
    asientosDeDeposito(dep).forEach((a) => {
      if (!a.caja || !isInRange(a.fecha, movs.desde, movs.hasta, movs.timeZone)) return;
      fila(a.metodo).depositos += a.caja;
    });
  });
  d.retiros.forEach((r) => {
    fila(r.metodo).retiros += r.monto;
  });

  const lista = Object.values(filas).map((f) => ({
    ...f,
    neto: f.ingresos - f.reembolsos - f.egresos + f.depositos - f.retiros,
  }));
  const ingresosTotal = lista.reduce((s, f) => s + f.ingresos, 0);
  // "Sin método" siempre al final: es lo que hay que corregir, no un método.
  lista.sort(
    (a, b) =>
      (a.key === SIN_METODO) - (b.key === SIN_METODO) ||
      b.ingresos - a.ingresos ||
      b.egresos - a.egresos
  );
  return {
    metodos: lista.map((f) => ({
      ...f,
      participacion: ingresosTotal > 0 ? f.ingresos / ingresosTotal : null,
    })),
    totales: lista.reduce(
      (t, f) => ({
        ingresos: t.ingresos + f.ingresos,
        reembolsos: t.reembolsos + f.reembolsos,
        egresos: t.egresos + f.egresos,
        depositos: t.depositos + f.depositos,
        retiros: t.retiros + f.retiros,
        neto: t.neto + f.neto,
      }),
      { ingresos: 0, reembolsos: 0, egresos: 0, depositos: 0, retiros: 0, neto: 0 }
    ),
    sinMetodo: filas[SIN_METODO] || null,
  };
}

// ── Inventario ─────────────────────────────────────────────────────────
// Los servicios que tienen piezas físicas: cuánto se invirtió en ellos y
// cuánto costaron de operar en el período (recargas, refacciones,
// mantenimiento — cualquier gasto ligado al servicio que no sea su compra).
//
// Ya no hay existencias ni consumo imputado: el inventario consumible se
// retiró porque exigía registrar cada consumo para no mentir. Lo que se
// gasta en una pieza se captura como gasto y se liga al servicio.
export function resumenInventario({ conceptos = [], costos = [], desde, hasta, timeZone }) {
  const enRango = (c) => isInRange(c?.fecha, desde, hasta, timeZone);

  const items = (conceptos || [])
    .filter((c) => !c?.archivedAt && llevaInventario(c))
    .map((item) => {
      const inversion = Number(item?.adquisicion?.monto) || 0;
      let gastado = 0;
      let gastosCount = 0;
      (costos || []).forEach((c) => {
        const ligado = c?.conceptoId === item.id;
        if (!ligado || !enRango(c) || c.origen === "adquisicion") return;
        gastado += Number(c.monto) || 0;
        gastosCount += 1;
      });

      return {
        id: item.id,
        nombre: item.nombre || "",
        unidad: unidadDe(item),
        inversion,
        gastado,
        gastosCount,
      };
    })
    .sort(
      (a, b) =>
        b.gastado - a.gastado ||
        b.inversion - a.inversion ||
        (a.nombre || "").localeCompare(b.nombre || "")
    );

  return {
    items,
    totales: {
      inversion: items.reduce((s, i) => s + i.inversion, 0),
      gastado: items.reduce((s, i) => s + i.gastado, 0),
    },
  };
}

// Uso de un servicio en los eventos del período (vía `recursosAsignados`
// denormalizado en el evento) y piezas comprometidas a futuro. Es lo que
// convierte "tengo 25 piezas" en "y se usaron así".
export function usoDeRecurso({ events = [], itemId, desde, hasta, timeZone, ahora = new Date() }) {
  let eventosPeriodo = 0;
  let asignacionesPeriodo = 0;
  let consumoPeriodo = 0;
  const comprometidas = new Set();
  let eventosFuturos = 0;
  // La disponibilidad es POR FECHA, no global: una unidad apartada para
  // octubre no está ocupada hoy. Lo que sí importa es el día más cargado:
  // cuántas unidades distintas se usan a la vez en un mismo día futuro.
  const porDia = {};

  (events || []).forEach((ev) => {
    if (!ev || ev.eventType !== "event" || ev.status === "cancelado") return;
    const entrada = (Array.isArray(ev.recursosAsignados) ? ev.recursosAsignados : []).find(
      (r) => r.recursoId === itemId
    );
    if (!entrada) return;
    if (isInRange(ev.startTime, desde, hasta, timeZone, INSTANTE)) {
      eventosPeriodo += 1;
      asignacionesPeriodo += (entrada.unidadIds || []).length;
      consumoPeriodo += Number(entrada.cantidad) || 0;
    }
    const start = ev.startTime?.toDate
      ? ev.startTime.toDate()
      : ev.startTime
      ? new Date(ev.startTime)
      : null;
    if (start && start >= ahora) {
      eventosFuturos += 1;
      (entrada.unidadIds || []).forEach((id) => id && comprometidas.add(id));
      const anchor = calendarDayAnchor(ev.startTime, timeZone, INSTANTE);
      if (anchor && (entrada.unidadIds || []).length > 0) {
        const key = anchor.toISOString().slice(0, 10);
        const dia = porDia[key] || (porDia[key] = { fecha: ev.startTime, unidades: new Set() });
        (entrada.unidadIds || []).forEach((id) => id && dia.unidades.add(id));
      }
    }
  });

  let picoDia = null;
  Object.values(porDia).forEach((d) => {
    if (!picoDia || d.unidades.size > picoDia.unidades) {
      picoDia = { fecha: d.fecha, unidades: d.unidades.size };
    }
  });

  return {
    eventosPeriodo,
    asignacionesPeriodo,
    consumoPeriodo,
    eventosFuturos,
    unidadesComprometidas: comprometidas.size,
    // Día futuro con más unidades en uso simultáneo (null si no hay).
    picoDia,
  };
}

// ── Gasto por proveedor ────────────────────────────────────────────────
// Llave del cubo que junta el gasto que no trae proveedor. Existe porque
// capturar un costo a mano —"Fotógrafo", "Sams", "Personal"— es lo normal:
// el proveedor del directorio solo se pega cuando el costo nace de un
// servicio o de una comisión de planner. Antes esos costos se tiraban en
// silencio y un venue que captura todo a mano abría el reporte en blanco
// mientras el resto del panel le contaba miles de pesos de gasto.
export const SIN_PROVEEDOR = "__sin_proveedor__";

export function gastoPorProveedor({ costos = [], desde, hasta, timeZone, limite = 8 }) {
  const map = {};
  costos.forEach((c) => {
    if (!c) return;
    if (!isInRange(c.fecha, desde, hasta, timeZone)) return;
    const key = c.proveedorId || SIN_PROVEEDOR;
    const acc =
      map[key] ||
      (map[key] = {
        proveedorId: key,
        nombre:
          key === SIN_PROVEEDOR
            ? "Sin proveedor asignado"
            : c.proveedorNombre || c.proveedorId,
        total: 0,
        count: 0,
        eventIds: new Set(),
        sueltos: 0,
      });
    acc.total += monto(c);
    acc.count += 1;
    // Lo que el owner quiere saber es "¿en cuántos eventos lo usé?", no
    // cuántos renglones de costo generó — un evento puede generar varios.
    if (c.eventId) acc.eventIds.add(c.eventId);
    else acc.sueltos += 1;
  });
  const filas = Object.values(map)
    .map(({ eventIds, ...a }) => ({ ...a, eventos: eventIds.size }))
    .sort((a, b) => b.total - a.total);
  // `limite` recorta la cola de proveedores, nunca el cubo sin asignar: es
  // justo el renglón que explica por qué el total no cuadra con la suma de
  // los de arriba. Se reinserta en el lugar que le toca por monto.
  const sin = filas.find((f) => f.proveedorId === SIN_PROVEEDOR);
  const reales = filas
    .filter((f) => f.proveedorId !== SIN_PROVEEDOR)
    .slice(0, limite);
  return sin
    ? [...reales, sin].sort((a, b) => b.total - a.total)
    : reales;
}

// ── Clientes ───────────────────────────────────────────────────────────
// La ficha de cliente (FRA-354) existe desde hace poco y ningún reporte la
// leía. Contesta tres preguntas: quién te deja más dinero, quién repite, y
// cuánto te deben entre todos.
//
// Las cifras por cliente salen de `resumenCliente`, el MISMO cálculo que
// pinta su ficha: si el reporte dijera otra cosa que la pantalla de donde
// vino el cliente, el owner dejaría de creerle a las dos.
//
// `cobertura` es el dato incómodo y por eso va: los eventos del período que
// no tienen ficha ligada no entran en ninguna fila de arriba.
export function resumenClientes({
  clientes = [],
  events = [],
  payments = [],
  desde,
  hasta,
  timeZone,
}) {
  const vivos = (clientes || []).filter((c) => c && !c.archivedAt);
  const enPeriodo = (ev) => isInRange(ev?.startTime, desde, hasta, timeZone, INSTANTE);
  const esEventoReal = (ev) =>
    ev && ev.eventType === "event" && ev.status !== "cancelado";

  const filas = vivos
    .map((cliente) => {
      const resumen = resumenCliente({ cliente, events, payments });
      const suyos = (events || []).filter(
        (ev) => esEventoReal(ev) && ev.clienteId === cliente.id
      );
      const delPeriodo = suyos.filter(enPeriodo);
      const valorPeriodo = delPeriodo.reduce((s, ev) => s + getValorEvento(ev), 0);
      // "Primero" y "último" por fecha de evento, no de captura: un cliente
      // de 2023 dado de alta ayer sigue siendo un cliente de 2023.
      const fechas = suyos
        .map((ev) => calendarDayAnchor(ev.startTime, timeZone, INSTANTE))
        .filter(Boolean)
        .sort((a, b) => a - b);
      return {
        id: cliente.id,
        nombre: nombreCliente(cliente),
        correo: cliente.correo || "",
        telefono: cliente.telefono || "",
        clientType: cliente.clientType || "fisica",
        eventos: suyos.length,
        eventosPeriodo: delPeriodo.length,
        valorTotal: resumen.valorTotal,
        valorPeriodo,
        pagado: resumen.pagado,
        debe: resumen.debe,
        cancelados: resumen.cancelados,
        primerEvento: fechas[0] || null,
        ultimoEvento: fechas[fechas.length - 1] || null,
        // Repite = dos o más eventos reales en toda su historia. No se mide
        // dentro del período: un cliente anual nunca repetiría en un mes.
        recurrente: suyos.length >= 2,
      };
    })
    .sort(
      (a, b) =>
        b.valorPeriodo - a.valorPeriodo ||
        b.valorTotal - a.valorTotal ||
        a.nombre.localeCompare(b.nombre)
    );

  const activos = filas.filter((f) => f.eventosPeriodo > 0);
  // Nuevo = su primer evento cae dentro del período. Recurrente = tuvo
  // evento en el período y ya tenía historia antes.
  const nuevos = activos.filter(
    (f) => f.primerEvento && f.primerEvento >= desde && f.primerEvento <= hasta
  );
  const eventosDelPeriodo = (events || []).filter(
    (ev) => esEventoReal(ev) && enPeriodo(ev)
  );
  const conFicha = eventosDelPeriodo.filter((ev) => !!ev.clienteId).length;
  const valorPeriodo = activos.reduce((s, f) => s + f.valorPeriodo, 0);
  const eventosPeriodo = activos.reduce((s, f) => s + f.eventosPeriodo, 0);

  return {
    filas,
    totales: {
      clientes: filas.length,
      activos: activos.length,
      nuevos: nuevos.length,
      recurrentes: activos.length - nuevos.length,
      valorPeriodo,
      eventosPeriodo,
      ticketPromedio: eventosPeriodo > 0 ? valorPeriodo / eventosPeriodo : 0,
      debe: filas.reduce((s, f) => s + f.debe, 0),
      conRecurrencia: filas.filter((f) => f.recurrente).length,
    },
    cobertura: {
      eventos: eventosDelPeriodo.length,
      conFicha,
      sinFicha: eventosDelPeriodo.length - conFicha,
    },
  };
}

// ── Cartera: antigüedad de lo que te deben y de lo que debes ───────────
// Cuentas ya lista por cobrar y deudas, pero sin edad: "$400,000 por
// cobrar" se lee igual de bien si vence el mes que entra que si lleva
// cuatro meses podrido. Aquí la edad es el reporte.
//
// El ancla de cada lado es distinta a propósito:
//   - Por cobrar envejece desde la FECHA DEL EVENTO — el saldo se liquida a
//     más tardar ese día, así que un evento futuro no está vencido, está
//     por vencer.
//   - Las deudas envejecen desde la fecha del costo (su causación), que es
//     cuando naciste debiendo.
// El pagado sale de `totalPagado` del evento, el mismo número que usa
// Cuentas → Por cobrar; si el reporte usara otra fuente, las dos pantallas
// dirían cosas distintas del mismo saldo.

export const BUCKETS_ANTIGUEDAD = [
  { key: "porVencer", label: "Por vencer", max: null },
  { key: "d0", label: "1 a 30 días", max: 30 },
  { key: "d30", label: "31 a 60 días", max: 60 },
  { key: "d60", label: "61 a 90 días", max: 90 },
  { key: "d90", label: "Más de 90 días", max: Infinity },
];

const diasEntre = (desdeFecha, hastaFecha) =>
  Math.floor((hastaFecha - desdeFecha) / 86400000);

// dias < 0 ⇒ todavía no vence. El día 0 (hoy) ya cuenta como vencido: el
// evento fue hoy y el saldo sigue vivo.
export function bucketAntiguedad(dias) {
  if (dias == null || dias < 0) return "porVencer";
  if (dias <= 30) return "d0";
  if (dias <= 60) return "d30";
  if (dias <= 90) return "d60";
  return "d90";
}

const bucketsVacios = () => {
  const out = {};
  BUCKETS_ANTIGUEDAD.forEach((b) => {
    out[b.key] = { key: b.key, label: b.label, total: 0, count: 0 };
  });
  return out;
};

function agrupaPorAntiguedad(filas) {
  const buckets = bucketsVacios();
  filas.forEach((f) => {
    const b = buckets[f.bucket];
    b.total += f.saldo;
    b.count += 1;
  });
  const lista = BUCKETS_ANTIGUEDAD.map((b) => buckets[b.key]);
  const total = filas.reduce((s, f) => s + f.saldo, 0);
  const vencido = lista
    .filter((b) => b.key !== "porVencer")
    .reduce((s, b) => s + b.total, 0);
  return { filas, buckets: lista, total, vencido };
}

export function carteraPorCobrar({ events = [], hoy = new Date(), timeZone }) {
  const hoyAncla = calendarDayAnchor(hoy, timeZone);
  const filas = (events || [])
    .filter((ev) => ev && ev.eventType === "event" && ev.status !== "cancelado")
    .map((ev) => {
      const valor = getValorEvento(ev);
      const pagado = Number(ev.totalPagado || 0);
      const saldo = valor - pagado;
      const ancla = calendarDayAnchor(ev.startTime, timeZone, INSTANTE);
      const dias = ancla && hoyAncla ? diasEntre(ancla, hoyAncla) : null;
      return {
        id: ev.id,
        nombre: ev.nombre || "Sin nombre",
        clienteId: ev.clienteId || null,
        cliente: ev.nombreCliente || ev.correo || "",
        fecha: ev.startTime,
        valor,
        pagado,
        saldo,
        dias,
        bucket: bucketAntiguedad(dias),
      };
    })
    // Sobrepagos y saldos liquidados no son cartera: viven en Por cobrar
    // como inconsistencia, no como antigüedad.
    .filter((f) => f.saldo > 0.01)
    .sort((a, b) => (b.dias ?? -Infinity) - (a.dias ?? -Infinity) || b.saldo - a.saldo);
  return agrupaPorAntiguedad(filas);
}

export function carteraDeudas({ costos = [], hoy = new Date(), timeZone }) {
  const hoyAncla = calendarDayAnchor(hoy, timeZone);
  const filas = (costos || [])
    .filter((c) => c && (c.estado || "por_pagar") === "por_pagar")
    .map((c) => {
      const ancla = calendarDayAnchor(c.fecha, timeZone);
      const dias = ancla && hoyAncla ? diasEntre(ancla, hoyAncla) : null;
      return {
        id: c.id,
        concepto: c.concepto || c.categoria || "Sin concepto",
        categoria: c.categoria || "Otro",
        proveedorId: c.proveedorId || null,
        proveedor: c.proveedorNombre || "",
        eventId: c.eventId || null,
        fecha: c.fecha,
        saldo: monto(c),
        dias,
        bucket: bucketAntiguedad(dias),
      };
    })
    .filter((f) => f.saldo > 0.01)
    .sort((a, b) => (b.dias ?? -Infinity) - (a.dias ?? -Infinity) || b.saldo - a.saldo);
  return agrupaPorAntiguedad(filas);
}

// ── Estructura de costos: fijo vs. variable ───────────────────────────
// La pregunta que contesta: "si no vendo un solo evento el mes que entra,
// ¿cuánto me cuesta abrir?". Tres familias, por la procedencia del costo:
//   - fijo: nació de un gasto recurrente (origen "recurrente", FRA-356);
//   - evento: cuelga de un evento (`eventId`), luego escala con la venta;
//   - general: gasto suelto del venue que nadie programó.
// Se ancla por fecha de causación (no de pago): es la estructura del
// período, no su flujo de caja — para eso está Flujo de caja.
export function estructuraCostos({ costos = [], desde, hasta, timeZone }) {
  const familias = {
    fijo: { key: "fijo", label: "Fijos", total: 0, count: 0 },
    evento: { key: "evento", label: "De eventos", total: 0, count: 0 },
    general: { key: "general", label: "Generales", total: 0, count: 0 },
  };
  const porCategoria = {};
  let porPagar = 0;
  let pagado = 0;

  (costos || []).forEach((c) => {
    if (!isInRange(c?.fecha, desde, hasta, timeZone)) return;
    const m = monto(c);
    const familia =
      c.origen === "recurrente" ? "fijo" : c.eventId ? "evento" : "general";
    familias[familia].total += m;
    familias[familia].count += 1;

    const cat = c.categoria || "Otro";
    const acc =
      porCategoria[cat] ||
      (porCategoria[cat] = { categoria: cat, total: 0, count: 0, fijo: 0, evento: 0, general: 0 });
    acc.total += m;
    acc.count += 1;
    acc[familia] += m;

    if ((c.estado || "por_pagar") === "pagado") pagado += m;
    else porPagar += m;
  });

  const total = Object.values(familias).reduce((s, f) => s + f.total, 0);
  return {
    familias: [familias.fijo, familias.evento, familias.general],
    porCategoria: Object.values(porCategoria).sort((a, b) => b.total - a.total),
    total,
    pagado,
    porPagar,
    // Cuánto del gasto del período NO depende de vender. Sin costos, null
    // (y no 0%): no es que no haya carga fija, es que no hay nada medido.
    pesoFijo: total > 0 ? familias.fijo.total / total : null,
  };
}

// Compromiso mensual vigente: lo que los gastos recurrentes activos suman
// al mes, normalizando cada periodicidad. No lee costos — es el "¿cuánto me
// cuesta abrir?" según lo configurado, no según lo capturado.
const MESES_POR_PERIODO = {
  semanal: 52 / 12,
  quincenal: 24 / 12,
  mensual: 1,
  bimestral: 1 / 2,
  trimestral: 1 / 3,
  semestral: 1 / 6,
  anual: 1 / 12,
};

export function compromisoMensual(gastosRecurrentes = []) {
  return (gastosRecurrentes || [])
    .filter((r) => r && r.activo !== false && !r.archivedAt)
    .reduce((s, r) => {
      const factor = MESES_POR_PERIODO[r.periodicidad] ?? 1;
      return s + (Number(r.monto) || 0) * factor;
    }, 0);
}

// ── Planners y comisiones ──────────────────────────────────────────────
// Un planner no te cobra tarifa, te cobra comisión (FRA-355), así que la
// pregunta no es "cuánto le pagué" sino "cuánto negocio me trajo y a qué
// costo". El evento se le atribuye por `plannerId` —el planner del evento,
// uno solo— y la comisión por los costos con categoría de comisión ligados
// a él.
//
// Ojo con los dos anclajes: los eventos del período se cuentan por su fecha
// (cuándo ocurre el negocio) y las comisiones por la fecha del costo. Un
// evento de enero con comisión capturada en diciembre aparece en los dos
// meses, cada cifra en el suyo.
export const esPlanner = (p) => !!p && (p.isPlanner || p.categoria === "planner");

export function resumenPlanners({
  proveedores = [],
  costos = [],
  events = [],
  desde,
  hasta,
  timeZone,
}) {
  const planners = (proveedores || []).filter((p) => esPlanner(p) && !p.archivedAt);

  const filas = planners
    .map((planner) => {
      const suyos = (events || []).filter(
        (ev) =>
          ev &&
          ev.eventType === "event" &&
          ev.status !== "cancelado" &&
          ev.plannerId === planner.id
      );
      const delPeriodo = suyos.filter((ev) =>
        isInRange(ev.startTime, desde, hasta, timeZone, INSTANTE)
      );
      const valorPeriodo = delPeriodo.reduce((s, ev) => s + getValorEvento(ev), 0);

      const comisiones = (costos || []).filter(
        (c) =>
          esComisionPlanner(c) &&
          c.proveedorId === planner.id &&
          isInRange(c.fecha, desde, hasta, timeZone)
      );
      const comisionTotal = comisiones.reduce((s, c) => s + monto(c), 0);
      const comisionPagada = comisiones
        .filter((c) => (c.estado || "por_pagar") === "pagado")
        .reduce((s, c) => s + monto(c), 0);

      return {
        id: planner.id,
        nombre: planner.nombre || "Sin nombre",
        acuerdo: planner.comision || null,
        eventos: suyos.length,
        eventosPeriodo: delPeriodo.length,
        valorPeriodo,
        comisionTotal,
        comisionPagada,
        comisionPorPagar: comisionTotal - comisionPagada,
        // Lo que de verdad te dejó el negocio que trajo, ya descontada su
        // comisión. No descuenta los demás costos del evento: eso es la
        // rentabilidad del evento, no la del planner.
        valorNeto: valorPeriodo - comisionTotal,
        // Comisión efectiva sobre lo que trajo. Null sin valor: dividir
        // entre cero diría "0%" y sonaría a que trabaja gratis.
        tasaEfectiva: valorPeriodo > 0 ? comisionTotal / valorPeriodo : null,
      };
    })
    .filter((f) => f.eventosPeriodo > 0 || f.comisionTotal > 0 || f.eventos > 0)
    .sort(
      (a, b) =>
        b.valorPeriodo - a.valorPeriodo ||
        b.comisionTotal - a.comisionTotal ||
        a.nombre.localeCompare(b.nombre)
    );

  const valorPeriodo = filas.reduce((s, f) => s + f.valorPeriodo, 0);
  const comisionTotal = filas.reduce((s, f) => s + f.comisionTotal, 0);
  return {
    filas,
    totales: {
      planners: filas.length,
      conEventoEnPeriodo: filas.filter((f) => f.eventosPeriodo > 0).length,
      eventosPeriodo: filas.reduce((s, f) => s + f.eventosPeriodo, 0),
      valorPeriodo,
      comisionTotal,
      comisionPagada: filas.reduce((s, f) => s + f.comisionPagada, 0),
      comisionPorPagar: filas.reduce((s, f) => s + f.comisionPorPagar, 0),
      tasaEfectiva: valorPeriodo > 0 ? comisionTotal / valorPeriodo : null,
    },
  };
}

// Comisiones mes a mes (mismo contrato que serieMensual: la página arma la
// lista de "YYYY-MM" y aquí solo se bucketiza). Por fecha del costo.
export function comisionesPorMes({
  meses = [],
  costos = [],
  proveedorId = null,
  timeZone,
  getMonthRange,
}) {
  return meses.map((mesKey) => {
    const { from, to } = getMonthRange(mesKey);
    const delMes = (costos || []).filter(
      (c) =>
        esComisionPlanner(c) &&
        (!proveedorId || c.proveedorId === proveedorId) &&
        isInRange(c.fecha, from, to, timeZone)
    );
    const total = delMes.reduce((s, c) => s + monto(c), 0);
    const pagado = delMes
      .filter((c) => (c.estado || "por_pagar") === "pagado")
      .reduce((s, c) => s + monto(c), 0);
    return {
      mes: mesKey,
      total,
      pagado,
      porPagar: total - pagado,
      comisiones: delMes.length,
      planners: new Set(delMes.map((c) => c.proveedorId).filter(Boolean)).size,
    };
  });
}

// ── Ocupación por espacio ─────────────────────────────────────────────
// Home ya contesta "qué tan lleno estuve" para el venue entero; lo que
// falta —y solo existe desde multiespacio (FRA-292)— es "y cuál de mis
// salones carga el negocio".
//
// Regla heredada de utils/espacios.js que aquí importa: un evento SIN
// footprint (`unidadesOcupadas` vacío) ocupa TODO el venue, así que cuenta
// para todos los espacios. Se devuelve aparte cuántos son, porque un venue
// con la mitad de sus eventos así tiene un desglose que no significa nada.
export function ocupacionPorEspacio({
  events = [],
  espacios = [],
  desde,
  hasta,
  timeZone,
}) {
  const reales = (events || []).filter(
    (ev) =>
      ev &&
      ev.eventType === "event" &&
      ev.status !== "cancelado" &&
      isInRange(ev.startTime, desde, hasta, timeZone, INSTANTE)
  );
  const sinFootprint = reales.filter(
    (ev) => !Array.isArray(ev.unidadesOcupadas) || ev.unidadesOcupadas.length === 0
  );

  const filas = (espacios || []).map((espacio) => {
    const unidades = Array.isArray(espacio.unidades) && espacio.unidades.length > 0
      ? espacio.unidades
      : [espacio.id];
    const propios = reales.filter((ev) => {
      const fp = Array.isArray(ev.unidadesOcupadas) ? ev.unidadesOcupadas : [];
      if (fp.length === 0) return true; // ocupa todo el venue
      return unidades.some((u) => fp.includes(u));
    });
    const valor = propios.reduce((s, ev) => s + getValorEvento(ev), 0);
    const dias = new Set();
    propios.forEach((ev) => {
      const ancla = calendarDayAnchor(ev.startTime, timeZone, INSTANTE);
      if (ancla) dias.add(ancla.toISOString().slice(0, 10));
    });
    return {
      id: espacio.id,
      nombre: espacio.nombre || espacio.id,
      eventos: propios.length,
      dias: dias.size,
      valor,
      // Valor por evento: un salón chico con muchos eventos baratos y uno
      // grande con pocos caros se distinguen aquí, no en el conteo.
      valorPorEvento: propios.length > 0 ? valor / propios.length : 0,
    };
  })
  .sort((a, b) => b.eventos - a.eventos || b.valor - a.valor);

  return {
    filas,
    eventos: reales.length,
    sinFootprint: sinFootprint.length,
    valor: reales.reduce((s, ev) => s + getValorEvento(ev), 0),
  };
}

// ── Agenda vendida (backlog) ───────────────────────────────────────────
// Lo ya vendido que todavía NO ocurre, mes por mes hacia adelante. Nace de
// que muchos venues están llenos hasta el año que entra y la única forma de
// verlo era el Estado de resultados, cambiando el año a mano y leyendo los
// renglones "por venir" de dos tablas distintas.
//
// NO trae utilidad, a propósito y por la misma razón que el Estado de
// resultados pinta "—" en los meses futuros: los costos de un evento que no
// ha ocurrido todavía no están capturados, y una utilidad calculada sobre
// costos a medias es una mentira optimista. Lo que sí se puede afirmar de un
// evento contratado es cuánto vale y cuánto de eso ya entró.
//
// El primer mes es PARCIAL: solo cuenta lo que queda de hoy en adelante, así
// que su cifra no es comparable con la de un mes completo y se marca.
export function agendaVendida({ events = [], hoy = new Date(), timeZone, tope = 24 }) {
  const hoyAncla = calendarDayAnchor(hoy, timeZone);
  const mesKey = (fecha) => {
    const a = calendarDayAnchor(fecha, timeZone, INSTANTE);
    if (!a) return null;
    return `${a.getUTCFullYear()}-${String(a.getUTCMonth() + 1).padStart(2, "0")}`;
  };

  const futuros = (events || [])
    .filter((ev) => ev && ev.eventType === "event" && ev.status !== "cancelado")
    .map((ev) => {
      const ancla = calendarDayAnchor(ev.startTime, timeZone, INSTANTE);
      const valor = getValorEvento(ev);
      // Un sobrepago no infla lo contratado: el cobrado se topa en el valor
      // para que cobrado + saldo sea exactamente la barra del mes. El
      // sobrepago como tal es una inconsistencia y vive en Por cobrar.
      const cobrado = Math.min(Number(ev.totalPagado || 0), valor);
      return {
        id: ev.id,
        nombre: ev.nombre || "Sin nombre",
        clienteId: ev.clienteId || null,
        cliente: ev.nombreCliente || ev.correo || "",
        startTime: ev.startTime,
        ancla,
        mes: mesKey(ev.startTime),
        valor,
        cobrado,
        saldo: Math.max(valor - cobrado, 0),
      };
    })
    .filter((f) => f.ancla && hoyAncla && f.ancla >= hoyAncla && f.mes);

  const porMes = {};
  futuros.forEach((f) => {
    const acc =
      porMes[f.mes] ||
      (porMes[f.mes] = { key: f.mes, eventos: 0, valor: 0, cobrado: 0, saldo: 0 });
    acc.eventos += 1;
    acc.valor += f.valor;
    acc.cobrado += f.cobrado;
    acc.saldo += f.saldo;
  });

  const claves = Object.keys(porMes).sort();
  const ultimoMes = claves.length > 0 ? claves[claves.length - 1] : null;

  // La serie arranca SIEMPRE en el mes en curso y corre hasta el último mes
  // con eventos: los meses vacíos de en medio se pintan en cero porque un
  // hueco en la agenda es justo lo que hay que ver.
  const meses = [];
  if (hoyAncla && ultimoMes) {
    let y = hoyAncla.getUTCFullYear();
    let m = hoyAncla.getUTCMonth();
    const [uy, um] = ultimoMes.split("-").map(Number);
    while (meses.length < tope) {
      const key = `${y}-${String(m + 1).padStart(2, "0")}`;
      meses.push(
        porMes[key]
          ? { ...porMes[key], parcial: meses.length === 0 }
          : { key, eventos: 0, valor: 0, cobrado: 0, saldo: 0, parcial: meses.length === 0 }
      );
      if (y === uy && m === um - 1) break;
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
  }

  const totales = meses.reduce(
    (acc, m) => ({
      eventos: acc.eventos + m.eventos,
      valor: acc.valor + m.valor,
      cobrado: acc.cobrado + m.cobrado,
      saldo: acc.saldo + m.saldo,
    }),
    { eventos: 0, valor: 0, cobrado: 0, saldo: 0 }
  );

  return {
    meses,
    totales,
    eventos: futuros.sort((a, b) => a.ancla - b.ancla),
    ultimoMes,
    mesesConEventos: claves.length,
    // Verdadero cuando `tope` cortó la serie antes del último mes vendido:
    // la UI lo dice en vez de fingir que la agenda termina ahí.
    truncada: !!ultimoMes && meses.length > 0 && meses[meses.length - 1].key !== ultimoMes,
  };
}
